import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const CompanyStorySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.3 });

  const milestones = [
    {
      year: "2019",
      title: "Foundation",
      description: "DARAJA.LABS was founded with a vision to bridge the gap between complex technology and business success."
    },
    {
      year: "2020",
      title: "First Major Client",
      description: "Secured our first enterprise client, delivering a comprehensive digital transformation solution."
    },
    {
      year: "2021",
      title: "Team Expansion",
      description: "Grew our team to 25+ specialists across development, design, and cybersecurity domains."
    },
    {
      year: "2022",
      title: "Global Reach",
      description: "Expanded operations internationally, serving clients across North America and Europe."
    },
    {
      year: "2023",
      title: "Innovation Hub",
      description: "Established our innovation lab focusing on AI, blockchain, and emerging technologies."
    },
    {
      year: "2024",
      title: "Industry Recognition",
      description: "Recognized as a leading IT services provider with multiple industry awards and certifications."
    }
  ];

  return (
    <section ref={ref} className="py-20 lg:py-32 bg-background relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/10 to-transparent" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16 lg:mb-24"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Our Journey of
            <span className="text-accent block lg:inline lg:ml-4">Innovation</span>
          </h2>
          <p className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            From a small startup to a recognized leader in digital transformation, our story is one of continuous growth, innovation, and unwavering commitment to client success.
          </p>
        </motion.div>

        {/* Company Values */}
        <motion.div
          className="grid md:grid-cols-3 gap-8 mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="text-center p-8 bg-card rounded-2xl border border-border hover:border-accent/50 transition-all duration-300">
            <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="w-8 h-8 bg-accent rounded-full" />
            </div>
            <h3 className="text-xl font-semibold text-card-foreground mb-4">Innovation First</h3>
            <p className="text-muted-foreground leading-relaxed">
              We embrace modern technologies and methodologies to deliver solutions that push the boundaries of what's possible.
            </p>
          </div>

          <div className="text-center p-8 bg-card rounded-2xl border border-border hover:border-accent/50 transition-all duration-300">
            <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="w-8 h-8 bg-accent rounded-full" />
            </div>
            <h3 className="text-xl font-semibold text-card-foreground mb-4">Client Success</h3>
            <p className="text-muted-foreground leading-relaxed">
              Your success is our success. We build long-term partnerships based on trust, transparency, and exceptional results.
            </p>
          </div>

          <div className="text-center p-8 bg-card rounded-2xl border border-border hover:border-accent/50 transition-all duration-300">
            <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="w-8 h-8 bg-accent rounded-full" />
            </div>
            <h3 className="text-xl font-semibold text-card-foreground mb-4">Quality Excellence</h3>
            <p className="text-muted-foreground leading-relaxed">
              We maintain the highest standards in everything we do, from code quality to customer service and project delivery.
            </p>
          </div>
        </motion.div>

        {/* Timeline */}
        <motion.div
          className="relative"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h3 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-12">
            Our <span className="text-accent">Milestones</span>
          </h3>

          {/* Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-accent via-accent/50 to-transparent hidden lg:block" />

          <div className="space-y-12 lg:space-y-16">
            {milestones?.map((milestone, index) => (
              <motion.div
                key={milestone?.year}
                className={`flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
              >
                {/* Content */}
                <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'} text-center lg:text-left`}>
                  <div className="bg-card p-6 lg:p-8 rounded-2xl border border-border hover:border-accent/50 transition-all duration-300">
                    <div className="text-accent text-lg font-bold mb-2">{milestone?.year}</div>
                    <h4 className="text-xl lg:text-2xl font-semibold text-card-foreground mb-4">
                      {milestone?.title}
                    </h4>
                    <p className="text-muted-foreground leading-relaxed">
                      {milestone?.description}
                    </p>
                  </div>
                </div>

                {/* Timeline Node */}
                <div className="relative">
                  <div className="w-4 h-4 bg-accent rounded-full border-4 border-background shadow-lg" />
                  <div className="absolute inset-0 w-4 h-4 bg-accent rounded-full animate-ping opacity-20" />
                </div>

                {/* Spacer for alternating layout */}
                <div className="flex-1 hidden lg:block" />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CompanyStorySection;